import { useEffect, useRef } from 'react';

/*
 * Keeps the screen's `--filters` CSS variable equal to the real rendered
 * height of everything above its .beacon-table, so the global
 * `height: calc(100dvh - @headerHeight - var(--filters))` (basics.less)
 * fits the table to the viewport no matter how many rows the filter area
 * wraps to. Attach the returned ref to the screen's root element; pass
 * deps that remount or re-layout that element.
 */
const useFiltersHeight = (deps: any[]) => {
    const screenRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const screen = screenRef.current;
        if (!screen) {
            return;
        }
        // measure from the scrolling content pane (.col-fluid), not the
        // screen div - margins/padding above the screen count too
        const getScrollParent = (): HTMLElement | null => {
            let el = screen.parentElement;
            while (el && el !== document.body) {
                const overflowY = getComputedStyle(el).overflowY;
                if (overflowY === 'auto' || overflowY === 'scroll') {
                    return el;
                }
                el = el.parentElement;
            }
            return null;
        };
        const apply = () => {
            const table = screen.querySelector('.ReactTable.beacon-table');
            if (!table) {
                return;
            }
            const scrollParent = getScrollParent();
            const base = scrollParent || screen;
            const baseTop =
                base.getBoundingClientRect().top -
                (scrollParent ? scrollParent.scrollTop : 0);
            const offset = Math.round(
                table.getBoundingClientRect().top - baseTop
            );
            if (offset > 0) {
                screen.style.setProperty('--filters', `${offset}px`);
            }
        };
        apply();
        // ResizeObserver is missing from this TS lib version's typings
        const RO = (window as any).ResizeObserver;
        const observer = RO ? new RO(apply) : undefined;
        if (observer) {
            observer.observe(screen);
        }
        window.addEventListener('resize', apply);
        return () => {
            if (observer) {
                observer.disconnect();
            }
            window.removeEventListener('resize', apply);
        };
        // eslint-disable-next-line
    }, deps);

    return screenRef;
};

export default useFiltersHeight;
